import { useNavigate, useSearch } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback } from "react"
import type { z } from "zod"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { CompanySearchFilter } from "@/screens/Companies/components/filters/company/CompanySearchFilter"
import { ContactSearchFilter } from "@/screens/Companies/components/filters/contact/ContactSearchFilter"
import { LocationSearchFilter } from "@/screens/Companies/components/filters/location/LocationSearchFilter"
import { SearchCategory } from "@/screens/Companies/types/enums"
import { CompanyFilterSchema, ContactFilterSchema, LocationFilterSchema } from "@/screens/Companies/types/filters"
import { COMPANY_EXPLICIT_EMPTY_FILTER_KEYS } from "@/screens/Companies/types/searchParams"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

interface SearchFilterProps {
  showFilterDrawer: boolean
  toggleDrawer: () => void
}

interface BaseFilterSchema {
  query?: string | null | undefined
  field: string
  order: string
  category: SearchCategory
}

type FilterSchema =
  | (z.infer<typeof CompanyFilterSchema> & BaseFilterSchema)
  | (z.infer<typeof ContactFilterSchema> & BaseFilterSchema)
  | (z.infer<typeof LocationFilterSchema> & BaseFilterSchema & { isLocationActive?: boolean })

export const FilterDrawer: FC<SearchFilterProps> = ({ showFilterDrawer, toggleDrawer }) => {
  const navigate = useNavigate({ from: "/app/companies" })
  const currentSearch = useSearch({
    from: "/app/companies/",
    strict: true,
  })

  const handleClearFilters = useCallback(() => {
    const { query, field, order, category } = currentSearch
    let clearedParams: FilterSchema = { query, field, order, category } as FilterSchema

    switch (category) {
      case SearchCategory.COMPANY:
        clearedParams = { ...CompanyFilterSchema.parse(clearedParams), field, order, category }
        break
      case SearchCategory.CONTACT:
        clearedParams = { ...ContactFilterSchema.parse(clearedParams), field, order, category }
        break
      case SearchCategory.LOCATION:
        clearedParams = { ...LocationFilterSchema.parse(clearedParams), field, order, category }
        break
      default:
        clearedParams = { query, field, order, category }
    }

    // Convert null values to undefined for TanStack Router compatibility
    const sanitizedParams = Object.fromEntries(
      Object.entries(clearedParams).map(([key, value]) => [key, value === null ? undefined : value])
    )

    const params = getRefinedSearchParams(
      sanitizedParams,
      {},
      { preserveEmptyArrayKeys: COMPANY_EXPLICIT_EMPTY_FILTER_KEYS }
    )
    void navigate(params)
  }, [currentSearch, navigate])

  return (
    <SearchFilterDrawer toggleDrawer={toggleDrawer} open={showFilterDrawer} clear={handleClearFilters}>
      {currentSearch.category === SearchCategory.COMPANY && <CompanySearchFilter />}
      {currentSearch.category === SearchCategory.LOCATION && <LocationSearchFilter />}
      {currentSearch.category === SearchCategory.CONTACT && <ContactSearchFilter />}
    </SearchFilterDrawer>
  )
}
